/**
 * LESSON: Vue Custom Directives
 *
 * In beauty-box, FadeinOnScroll.js used GSAP + ScrollTrigger (jQuery, global
 * imports, imperative DOM manipulation). In Vue we replace that with:
 *   1. A custom directive (v-fade-in) that attaches an IntersectionObserver
 *      to any element that needs the animation.
 *   2. CSS transitions to do the actual visual effect.
 *
 * A directive is the right tool here because it is about *DOM behavior*, not
 * component state. You register it globally on the Vue app instance, then use
 * `v-fade-in` on any element in any template — exactly like how `[anim='slideIn']`
 * worked as an HTML attribute in the old project.
 *
 * Lifecycle hooks on a directive:
 *   mounted  — element is in the DOM, safe to interact with it
 *   unmounted — element removed, clean up observers to avoid memory leaks
 */
export const fadeInOnScrollDirective = {
  mounted(el, binding) {
    // binding.value can be 'slideIn' | 'zoomIn' | 'fromLeft' | 'fromRight' | 'fadeIn'
    const animType = binding.value || 'slideIn';
    el.classList.add('anim-ready', `anim--${animType}`);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.classList.add('anim-visible');
            observer.unobserve(el); // once: true equivalent
          }
        });
      },
      { rootMargin: '0px 0px -50px 0px' },
    );

    observer.observe(el);
    // Store the observer so unmounted can clean up
    el._fadeInObserver = observer;
  },

  unmounted(el) {
    if (el._fadeInObserver) {
      el._fadeInObserver.disconnect();
    }
  },
};
